import { connect, useDispatch } from 'react-redux';
import Select from '@atlaskit/select';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { fetchQuotesPost } from '../duck/actions/orders';
import { fetchSearchForContactPost } from '../duck/actions/invoices';
import SearchIcon from '@atlaskit/icon/glyph/search';
import { Dimensions } from '../../../components/visuals';
import { colors } from '@atlaskit/theme';

import { MACTableType } from '../table/TableContent';
import Button from '@atlaskit/button';
import { OrderSearchFiltersForPagination } from '../OrdersPage';
import { StyledTextfield } from './styledComponents';

const SearchContainer = styled.div`
  display: flex;
  gap: ${Dimensions.units(0.25)}px;
`;

const SearchButton = styled(Button)`
  border: 1px solid ${colors.N100} !important;
  height: 100% !important;
  padding: 0px 5px !important;
`;

export enum SearchType {
  ORDER_NUMBER = 'orderNumber',
  TOTAL_PRICE = 'totalPrice',
  COMPANY_DOMAIN = 'companyDomain',
}

const searchOptions = [
  { value: SearchType.ORDER_NUMBER, label: 'Order number' },
  { value: SearchType.TOTAL_PRICE, label: 'Total price' },
  { value: SearchType.COMPANY_DOMAIN, label: 'Company domain' },
];

const selectStyles = {
  control: (provided) => ({
    ...provided,
    background: `${colors.N10}`,
    minHeight: `${Dimensions.units(3.75)}px`,
    width: `${Dimensions.units(20.5)}px`,
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: `${Dimensions.units(3.75)}px`,
  }),
};

type Props = {
  tableType: MACTableType;
  setSearchFiltersForPagination: (searchFilters: OrderSearchFiltersForPagination) => void;
  maxResults: number;
};

const OrderSearch = ({ tableType, setSearchFiltersForPagination, maxResults }: Props) => {
  const dispatch = useDispatch();
  const [selectedOption, setSelectedOption] = useState(searchOptions[0]);
  const [searchInputText, setSearchInputText] = useState('');
  const [placeholder, setPlaceholder] = useState('123456789');
  const [prevoiusSearchState, setPrevoiusSearchState] = useState({
    searchValue: '',
    selectedOption: searchOptions[0],
  });

  const handleSelectChange = (option) => {
    setSelectedOption(option);
    setSearchInputText('');
    switch (option.value) {
      case SearchType.COMPANY_DOMAIN:
        setPlaceholder('atlassian.com');
        break;
      case SearchType.TOTAL_PRICE:
        setPlaceholder('1000');
        break;
      default:
        setPlaceholder('123456789');
    }
  };
  const handleSearchInputTextChange = (e, option) => {
    let newValue = e.target.value;
    if (option.value === SearchType.ORDER_NUMBER || option.value === SearchType.TOTAL_PRICE) {
      newValue = newValue.replace(/\D/g, '');
    }
    setSearchInputText(newValue);
  };
  const submitSearchUpdate = (value, option) => {
    const orderNumberMask = option.value === SearchType.ORDER_NUMBER ? value : undefined;
    const integerPartOfTotalAmount = option.value === SearchType.TOTAL_PRICE ? value : undefined;
    const technicalContactCompanyDomainName =
      option.value === SearchType.COMPANY_DOMAIN ? value : undefined;

    setSearchFiltersForPagination({
      orderNumberMask,
      technicalContactCompanyDomainName,
      integerPartOfTotalAmount,
    });

    if (tableType === MACTableType.invoices) {
      dispatch(
        fetchSearchForContactPost(
          maxResults,
          0,
          orderNumberMask,
          technicalContactCompanyDomainName,
          integerPartOfTotalAmount,
        ),
      );
    } else {
      dispatch(
        fetchQuotesPost(
          maxResults,
          0,
          orderNumberMask,
          technicalContactCompanyDomainName,
          integerPartOfTotalAmount,
        ),
      );
    }
  };

  useEffect(() => {
    const prevState = prevoiusSearchState;
    setPrevoiusSearchState({
      searchValue: searchInputText,
      selectedOption,
    });
    setSearchInputText(prevState.searchValue);
    setSelectedOption(prevState.selectedOption);
  }, [tableType]);

  return (
    <>
      <SearchContainer>
        <Select
          options={searchOptions}
          defaultValue={searchOptions[0]}
          styles={selectStyles}
          onChange={handleSelectChange}
          value={selectedOption}
        />
        <StyledTextfield
          placeholder={placeholder}
          $height={4.25}
          $width={22.5}
          onKeyDown={(e) =>
            e.key === 'Enter' && submitSearchUpdate(searchInputText, selectedOption)
          }
          onChange={(e) => handleSearchInputTextChange(e, selectedOption)}
          value={searchInputText}
          data-cy="search-order-input"
        />
        <SearchButton
          data-cy="mac-search-order-btn"
          onClick={() => submitSearchUpdate(searchInputText, selectedOption)}
        >
          <SearchIcon size="small" primaryColor={colors.N300} label="" />
        </SearchButton>
      </SearchContainer>
    </>
  );
};

export default connect(null, null)(OrderSearch);
